import { useState } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { Box, TextField, Button, CircularProgress, Typography } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import LineGraph from '../graph/LineGraph';
import { IAchSettlement } from '@/stack-shared/interfaces/model/ach-settlement.interface';
import { useAchSettlements } from '@/hooks/api/useAchSettlements';
import AchSettlementTable from './AchSettlementTable';
import AbstractTable from '../table/AbstractTable';

function AchSettlementGraph(props: any) {
    const achSettlementsMutation = useAchSettlements();
    const [startDate, setStartDate] = useState<Dayjs | null>(
        dayjs().subtract(1, 'month').startOf('month')
    );
    const [endDate, setEndDate] = useState<Dayjs | null>(dayjs().subtract(1, 'month').endOf('month'));
    const [filteredData, setFilteredData] = useState<IAchSettlement[]>([]);
    const [showGraph, setShowGraph] = useState(false);
    const loading = achSettlementsMutation.isPending;

    // State for multi-select filters
    const [merchantFilter, setMerchantFilter] = useState<string[]>([]);
    const [descriptionFilter, setDescriptionFilter] = useState<string[]>([]);
    const [terminalFilter, setTerminalFilter] = useState<string[]>([]);

    // Extract unique options for filters, excluding null or undefined
    const merchantOptions = [...new Set(filteredData.map((item) => item.merchantId).filter((id) => id != null))];
    const descriptionOptions = [...new Set(filteredData.map((item) => item.description).filter((desc) => desc != null))];
    const terminalOptions = [...new Set(filteredData.map((item) => item.terminal).filter((term) => term != null))];
    const [summaryData, setSummaryData] = useState<any>([]);

    console.log('terminalOptions', terminalOptions);
    const fetchAchSettlements = async () => {
        if (!startDate || !endDate) return;

        achSettlementsMutation.mutate(
            {
                startDate: startDate.toISOString(),
                endDate: endDate.toISOString(),
            },
            {
                onSuccess: (data: any) => {
                    setFilteredData(data.achSettlements || data.data?.achSettlements || []);
                    setShowGraph(true);
                    setSummaryData(data.summary || data.data?.summary || []);
                },
                onError: (error) => {
                    console.error('Failed to fetch ACH settlements:', error);
                },
            }
        );
    };

    const handleDateChange = (setter: React.Dispatch<React.SetStateAction<Dayjs | null>>, newValue: Dayjs | null) => {
        setter(newValue);
        setShowGraph(false);
    };

    // Apply filters to the data
    const filteredGraphData = filteredData.filter((item: any) => {
        const matchesMerchant = merchantFilter.length === 0 || merchantFilter.includes(item.merchantId);
        const matchesDescription = descriptionFilter.length === 0 || descriptionFilter.includes(item.description);
        const matchesTerminal = terminalFilter.length === 0 || terminalFilter.includes(item.terminal);
        return matchesMerchant && matchesDescription && matchesTerminal;
    });

    const prepareChartData = (data: IAchSettlement[]) => ({
        labels: data.map((item: any) => dayjs(item.achDate)?.format('MM/DD/YYYY')),
        datasets: [
            {
                label: 'Dispensed',
                data: data.map((item) => item.dispensed || 0),
                borderColor: 'rgb(75, 192, 192)',
                tension: 0.1,
                fill: true,
            },
            {
                label: 'Surcharged',
                data: data.map((item) => item.surcharged || 0),
                borderColor: 'rgb(255, 99, 132)',
                tension: 0.1,
                fill: false,
            },
            {
                label: 'Surcharge Amount',
                data: data.map((item) => item.surchargeAmount || 0),
                borderColor: 'rgb(54, 162, 235)',
                tension: 0.1,
                fill: false,
            },
            {
                label: 'Surcharge Withdrawals',
                data: data.map((item) => item.surchargeWithdrawals || 0),
                borderColor: 'rgb(153, 102, 255)',
                tension: 0.1,
                fill: false,
            },
        ],
    });

    const renderAchGraph = () => {
        const chartData = prepareChartData(filteredGraphData);
        return (
            <div style={{ height: '400px', width: '100%' }}>
                <LineGraph
                    chartData={chartData}
                    uuid="achSettlementGraph"
                    title="ACH Settlement Data"
                    height={400}
                    width={600}
                    isCurrency={true}
                />
            </div>
        );
    };

    const columns = [
        {
            accessorKey: 'terminal',
            header: 'Terminal',
        },
        {
            accessorKey: 'dispensed',
            header: 'Dispensed',
            sum: true,
            format: 'currency'
        },
        {
            accessorKey: 'surcharged',
            header: 'Surcharged',
            sum: true,
            format: 'currency'
        },
        {
            accessorKey: 'surchargeAmount',
            header: 'Surcharge Amount',
            sum: true,
            format: 'currency'
        },
        {
            accessorKey: 'Surcharge Withdrawals',
            header: 'Surcharge Withdrawals',
            sum: true,
            format: 'currency'
        },
    ];

    return (
        <>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box display="flex" gap={2} marginBottom={3}>
                    <DatePicker
                        label="Start Date"
                        value={startDate}
                        onChange={(value) => handleDateChange(setStartDate, dayjs.isDayjs(value) ? value : null)}
                        slotProps={{ textField: { fullWidth: false } }}
                    />
                    <DatePicker
                        label="End Date"
                        value={endDate}
                        onChange={(value) => handleDateChange(setEndDate, dayjs.isDayjs(value) ? value : null)}
                        slotProps={{ textField: { fullWidth: false } }}
                    />
                </Box>
            </LocalizationProvider>
            <Box marginBottom={3}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={fetchAchSettlements}
                    disabled={loading}
                >
                    {loading ? "Loading..." : "Refresh Data"}
                </Button>
            </Box>


            {summaryData?.length > 0 && <AbstractTable showSearch={false} data={summaryData} columns={columns} />}


            <Box display="flex" gap={2} marginBottom={3}>
                <Autocomplete
                    style={{ width: 200 }}
                    multiple
                    options={merchantOptions}
                    value={merchantFilter}
                    onChange={(event, newValue) => setMerchantFilter(newValue as any)}
                    renderInput={(params) => <TextField {...params} label="Merchant ID" />}
                />
                <Autocomplete
                    style={{ width: 200 }}
                    multiple
                    options={descriptionOptions}
                    value={descriptionFilter}
                    onChange={(event, newValue) => setDescriptionFilter(newValue as any)}
                    renderInput={(params) => <TextField {...params} label="Description" />}
                />
                <Autocomplete
                    style={{ width: 200 }}
                    multiple
                    options={terminalOptions}
                    value={terminalFilter}
                    onChange={(event, newValue) => setTerminalFilter(newValue as any)}
                    renderInput={(params) => <TextField {...params} label="Terminal" />}
                />
            </Box>

            {loading ? (
                <CircularProgress />
            ) : (
                showGraph && <>
                    {renderAchGraph()}
                    <AchSettlementTable data={filteredGraphData} />
                </>
            )}
        </>
    );
}

export default AchSettlementGraph;
